import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { WorkoutCard } from './WorkoutCard'
import { cn } from '../../lib/utils'
import type { Workout, WorkoutResult } from '../../types/workout'
import type { WorkoutResultMatch } from '../../utils/match-workout-results'

interface WorkoutListProps {
  /** The day's workouts, already in the order they should be shown in. */
  workouts: Workout[]
  workoutMatches?: Map<string, WorkoutResultMatch>
  hideResults?: boolean
  /**
   * Whether the athlete may drag this list. Off while the week is being
   * scheduled — that mode is already about moving workouts, by day rather than
   * by order, and two drags on one card is one too many.
   */
  sortable: boolean
  /** The whole list in its new order. Only called when the order changed. */
  onReorder?: (ids: string[]) => void
  /** Rendered under each card; the day picker uses it in schedule mode. */
  renderFooter?: (workout: Workout) => ReactNode
}

interface SortableWorkoutProps {
  workout: Workout
  results: WorkoutResult[]
  completed: boolean
  hideResults: boolean
  draggable: boolean
  footer?: ReactNode
}

function SortableWorkout({
  workout,
  results,
  completed,
  hideResults,
  draggable,
  footer,
}: SortableWorkoutProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: workout.id,
    disabled: !draggable,
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex flex-col', isDragging && 'opacity-50 z-10')}
    >
      <div className="flex items-center gap-1">
        {draggable && (
          <button
            type="button"
            className="cursor-grab touch-none p-1 -ml-1 text-muted-foreground active:cursor-grabbing"
            aria-label={t('trainingDiary.workouts.reorder', 'Reorder {{name}}', {
              name: workout.exercise.title,
            })}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" aria-hidden />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <WorkoutCard
            workout={workout}
            results={results}
            completed={completed}
            hideResults={hideResults}
          />
        </div>
      </div>
      {footer}
    </div>
  )
}

/**
 * A day's workouts, in the order they are meant to be done, draggable into
 * another one. The handle only appears once there is something to order: on a
 * single-workout day it would be a control that does nothing.
 */
export function WorkoutList({
  workouts,
  workoutMatches,
  hideResults = false,
  sortable,
  onReorder,
  renderFooter,
}: WorkoutListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const draggable = sortable && workouts.length > 1 && Boolean(onReorder)

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const from = workouts.findIndex((w) => w.id === active.id)
    const to = workouts.findIndex((w) => w.id === over.id)
    if (from === -1 || to === -1) return

    onReorder?.(arrayMove(workouts, from, to).map((w) => w.id))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={workouts.map((w) => w.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1.5">
          {workouts.map((workout) => {
            const match = workoutMatches?.get(workout.id)
            return (
              <SortableWorkout
                key={workout.id}
                workout={workout}
                results={match?.results ?? []}
                completed={match?.completed ?? false}
                hideResults={hideResults}
                draggable={draggable}
                footer={renderFooter?.(workout)}
              />
            )
          })}
        </div>
      </SortableContext>
    </DndContext>
  )
}
